/**
 * HQFloorContent
 *
 * Renders the appropriate content for each floor in Headquarters.
 * Routes between Lobby (overview), HR (employees), Finance, and Strategy floors.
 */

import React from 'react';
import type { FloorId } from '../spatial/FloorSelector';
import { LobbyReception } from './LobbyReception';
import { OperationsEmployees } from '../tycoon/OperationsEmployees';
import { FinanceView } from '../tycoon/FinanceView';
import { StrategicPlanner } from '../tycoon/StrategicPlanner';
import { useBuildingStore } from '@/stores';
import type { CompanyState } from '@/types/company';
import type { Product, Employee, HiringProcess } from '@/types/tycoon';
import type { BuildingCapacity } from '@/types/buildings';
import type { EmployeeAllocation } from '@/services/tycoon/employeeAllocationService';
import type { ActiveEvent } from '@/types/dynamicEvents';

export interface HQFloorContentProps {
  /** Current floor to display */
  currentFloor: FloorId;
  /** Company state data */
  companyState: CompanyState;
  /** Company display name */
  companyName?: string;
  /** List of products */
  products: Product[];
  /** List of employees */
  employees: Employee[];
  /** Active hiring processes */
  hiringProcesses: HiringProcess[];
  /** Whether to show fire warning modal */
  showFireWarning: boolean;
  /** Callback to hire employees */
  onHire: (role: string, count: number) => void;
  /** Callback to fire employees */
  onFire: (role: string, count: number) => void;
  /** Callback to toggle fire warning */
  onToggleFireWarning: (show: boolean) => void;
  /** Building capacity info */
  buildingCapacity?: BuildingCapacity;
  /** Function to check if hiring can proceed */
  checkCapacityForHiring?: (role: string, count: number, hiringProcesses?: HiringProcess[]) => {
    hasCapacity: boolean;
    availableCapacity: number;
    reason?: string;
  };
  /** Employee allocations */
  employeeAllocations?: EmployeeAllocation[];
  /** Callback to update company state */
  onStateUpdate?: (updates: Partial<CompanyState>) => void;
  /** Event history for newspaper display */
  eventHistory?: ActiveEvent[];
}

export const HQFloorContent: React.FC<HQFloorContentProps> = ({
  currentFloor,
  companyState,
  companyName = '',
  products,
  employees,
  hiringProcesses,
  showFireWarning,
  onHire,
  onFire,
  onToggleFireWarning,
  buildingCapacity: propBuildingCapacity,
  checkCapacityForHiring: propCheckCapacity,
  employeeAllocations = [],
  onStateUpdate,
  eventHistory,
}) => {
  // Get building store for capacity checking
  const { getCapacity, checkCapacityForHiring: storeCheckCapacity } = useBuildingStore();

  // Use props if provided, otherwise use store
  const buildingCapacity = propBuildingCapacity ?? getCapacity(employees);
  const checkCapacityForHiring = propCheckCapacity ??
    ((role: string, count: number, processes?: HiringProcess[]) =>
      storeCheckCapacity(role, count, employees, processes ?? hiringProcesses));

  switch (currentFloor) {
    case 'lobby':
      return (
        <LobbyReception
          companyState={companyState}
          companyName={companyName}
          products={products}
          employees={employees}
          eventHistory={eventHistory}
          gameDate={{ day: companyState.day, month: companyState.month, year: companyState.year }}
        />
      );

    case 'hr':
      return (
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4 text-theme-text-primary">Human Resources</h2>
          <OperationsEmployees
            employees={employees}
            hiringProcesses={hiringProcesses}
            cash={companyState.cash}
            reputation={companyState.reputation}
            showFireWarning={showFireWarning}
            onHire={onHire}
            onFire={onFire}
            onToggleFireWarning={onToggleFireWarning}
            buildingCapacity={buildingCapacity}
            checkCapacityForHiring={checkCapacityForHiring}
            employeeAllocations={employeeAllocations}
          />
        </div>
      );

    case 'finance':
      return (
        <FinanceView
          companyState={{
            cash: companyState.cash,
            day: companyState.day,
            month: companyState.month,
            year: companyState.year,
            dailyStats: companyState.dailyStats,
          }}
          products={products}
        />
      );

    case 'strategy':
      return (
        <StrategicPlanner
          companyState={{
            cash: companyState.cash,
            reputation: companyState.reputation,
            marketShare: companyState.marketShare,
          }}
          onUpdateState={onStateUpdate || (() => {})}
        />
      );

    default:
      return null;
  }
};

export default HQFloorContent;
